import { type RelationEditor } from '@framework/form/form/relations/useRelationEditor.ts';
import { t } from '@framework/i18n/intl/i18next.ts';
import { message } from 'antd';
import { useCallback } from 'react';
import { type IMapGroup } from '../../../layers/MapGroup.ts';
import { type IMapLayer } from '../../../layers/MapLayer.ts';
import { type ILayerCatalogImportResult } from '../LayerCatalogImportModal.tsx';
import { mergeLayerCatalogEntries } from './mergeLayerCatalogEntries.ts';

/**
 * Opzioni per l'hook di importazione dal catalogo livelli.
 */
export interface IUseLayerCatalogImportOptions {
  groupEditor: RelationEditor<IMapGroup>;
  layersEditor: RelationEditor<IMapLayer>;
  onImport?: () => void;
}

/**
 * Hook che gestisce l'importazione di livelli e gruppi dal catalogo.
 * In modalità "replace" sostituisce tutti i livelli e gruppi esistenti;
 * in modalità "append" delega a {@link mergeLayerCatalogEntries} che
 * accoda i livelli e deduplica i gruppi per nome.
 */
export function useLayerCatalogImport(options: IUseLayerCatalogImportOptions) {
  const { groupEditor, layersEditor, onImport } = options;

  return useCallback(
    (result: ILayerCatalogImportResult) => {
      const { levels, mode } = result;

      if (mode === 'replace') {
        layersEditor.replace(levels.layers);
        groupEditor.replace(levels.groups);
        message.success(
          t('map-catalog.merge.replaced', 'Catalogo sostituito con successo'),
        );
      } else {
        mergeLayerCatalogEntries(layersEditor, groupEditor, levels);
      }

      onImport?.();
    },
    [layersEditor, groupEditor, onImport],
  );
}
